import { useState, useEffect, useCallback } from 'react';
import {
  fetchProperties, fetchWorkPlanSummary, fetchWorkPlan,
  inheritWorkPlan, updateWorkPlanItem, setTrackingMode,
  fetchWeeklyProgressWeeks, fetchWeeklyProgress, fetchPropertyWeeklyHistory,
  createWeeklyProgress, batchCreateWeeklyProgress, cloneWeekProgress, approveWeekProgress,
  createProgressWeek, changeWeekStatus, deleteProgressWeek, deletePropertyFromWeek,
  updateProgressWeek, migrateClosedWeeks,
  fetchWorkCatalog,
} from '@/lib/api';
import { formatDate, formatMXNCompact } from '@/lib/formatters';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  HardHat, TrendingUp, TrendingDown, AlertTriangle, Check, Clock, Home,
  ArrowRight, Download, ChevronLeft, ChevronDown, ChevronRight, CheckCircle2,
  Calendar, Plus, Copy, Trash2, Pencil,
} from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';


/* ══════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════ */
export default function AvanceObra() {
  const { can } = useAuth();
  const canEdit = can('avance_obra', 'edit');
  const [mainTab, setMainTab] = useState('general');
  const [properties, setProperties] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedProp, setSelectedProp] = useState(null);
  const [plan, setPlan] = useState([]);
  const [summary, setSummary] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const props = await fetchProperties();
      setProperties(props);
      const sums = await Promise.all(props.map(async p => {
        try {
          const s = await fetchWorkPlanSummary(p.id);
          return {
            ...p,
            has_plan: (s?.total_items || 0) > 0,
            projected: s?.projected_total || 0,
            actual: s?.actual_total || 0,
            variance: s?.variance || 0,
            status: s?.status || 'en_linea',
            last_update: s?.last_update || null,
            tracking_mode: s?.tracking_mode || 'programa',
          };
        } catch {
          return { ...p, has_plan: false, projected: 0, actual: 0, variance: 0, status: 'en_linea', last_update: null, tracking_mode: 'programa' };
        }
      }));
      setSummaries(sums);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = async (prop) => {
    setSelectedProp(prop);
    setDetailLoading(true);
    try {
      const [planData, sumData] = await Promise.all([
        fetchWorkPlan(prop.id),
        fetchWorkPlanSummary(prop.id),
      ]);
      setPlan(planData);
      setSummary(sumData);
    } catch { setPlan([]); setSummary(null); }
    finally { setDetailLoading(false); }
  };

  const closeDetail = () => { setSelectedProp(null); setPlan([]); setSummary(null); loadList(); };

  const handleInherit = async () => {
    try {
      const res = await inheritWorkPlan(selectedProp.id);
      toast.success(res.message || 'Plan sincronizado');
      await openDetail(selectedProp);
      loadList();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al sincronizar'); }
  };

  const handleUpdate = async (planId, field, value) => {
    try {
      await updateWorkPlanItem(planId, { [field]: value });
      // Re-fetch full enriched plan + summary to recalculate all weighted totals
      const [freshPlan, freshSummary] = await Promise.all([
        fetchWorkPlan(selectedProp.id),
        fetchWorkPlanSummary(selectedProp.id),
      ]);
      setPlan(freshPlan);
      setSummary(freshSummary);
    } catch { toast.error('Error al guardar'); }
  };

  const handleModeChange = async (mode) => {
    try {
      await setTrackingMode(selectedProp.id, mode);
      setSummary(s => s ? { ...s, tracking_mode: mode } : s);
      toast.success(mode === 'programa' ? 'Modo: Con programa' : 'Modo: Solo avance');
      loadList();
    } catch { toast.error('Error al cambiar modo'); }
  };

  /* ── DETAIL VIEW ── */
  if (selectedProp) {
    return (
      <DetailView
        prop={selectedProp}
        plan={plan}
        summary={summary}
        loading={detailLoading}
        onClose={closeDetail}
        onInherit={handleInherit}
        onUpdate={handleUpdate}
        onModeChange={handleModeChange}
      />
    );
  }

  /* ── LIST VIEW ── */
  return (
    <Tabs value={mainTab} onValueChange={setMainTab}>
      <TabsList className="mb-4">
        <TabsTrigger value="general"><HardHat className="h-3.5 w-3.5 mr-1.5" />Avance General</TabsTrigger>
        <TabsTrigger value="semanal"><Calendar className="h-3.5 w-3.5 mr-1.5" />Avance Semanal</TabsTrigger>
      </TabsList>
      <TabsContent value="general">
        <ListView
          summaries={summaries}
          loading={loading}
          filter={filter}
          onFilterChange={setFilter}
          onSelect={openDetail}
        />
      </TabsContent>
      <TabsContent value="semanal">
        <WeeklyProgressTab canEdit={canEdit} onSynced={loadList} />
      </TabsContent>
    </Tabs>
  );
}


/* ══════════════════════════════════════════════
   LIST VIEW — All Properties
   ══════════════════════════════════════════════ */
function ListView({ summaries, loading, filter, onFilterChange, onSelect }) {
  const withPlan = summaries.filter(r => r.has_plan);
  const filtered = filter === 'all' ? summaries
    : filter === 'sin_plan' ? summaries.filter(r => !r.has_plan)
    : filter === 'solo_avance' ? summaries.filter(r => r.has_plan && r.tracking_mode === 'avance')
    : summaries.filter(r => r.has_plan && r.tracking_mode !== 'avance' && r.status === filter);

  const atrasadas = withPlan.filter(r => r.status === 'atrasada' && r.tracking_mode !== 'avance').length;
  const adelantadas = withPlan.filter(r => r.status === 'adelantada' && r.tracking_mode !== 'avance').length;
  const enLinea = withPlan.filter(r => r.status === 'en_linea' && r.tracking_mode !== 'avance').length;
  const sinPlan = summaries.filter(r => !r.has_plan).length;
  const conPrograma = withPlan.filter(r => r.tracking_mode !== 'avance');
  const soloAvance = withPlan.filter(r => r.tracking_mode === 'avance');
  const avgProy = conPrograma.length > 0 ? conPrograma.reduce((s, r) => s + r.projected, 0) / conPrograma.length : 0;
  const avgReal = withPlan.length > 0 ? withPlan.reduce((s, r) => s + r.actual, 0) / withPlan.length : 0;

  if (loading) return (
    <div className="space-y-6" data-testid="avance-obra-loading">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <Skeleton className="h-[400px] rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="avance-obra-page">
      <PageHeader title="Avance de Obra" subtitle="Control de avance físico por vivienda" />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        <KPICard label="Con Plan" value={`${withPlan.length} de ${summaries.length}`} icon={HardHat} compact testId="kpi-con-plan" />
        <KPICard label="Prom. Proyectado" value={conPrograma.length > 0 ? `${avgProy.toFixed(1)}%` : '—'} icon={Clock} compact testId="kpi-prom-proy" />
        <KPICard label="Prom. Real" value={`${avgReal.toFixed(1)}%`} icon={TrendingUp} compact testId="kpi-prom-real" />
        <KPICard label="Atrasadas" value={atrasadas} icon={AlertTriangle} className={atrasadas > 0 ? 'border-red-200' : ''} compact testId="kpi-atrasadas" />
        <KPICard label="Adelantadas" value={adelantadas} icon={Check} className={adelantadas > 0 ? 'border-emerald-200' : ''} compact testId="kpi-adelantadas" />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Select value={filter} onValueChange={onFilterChange}>
          <SelectTrigger className="w-[200px] h-9" data-testid="filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas ({summaries.length})</SelectItem>
            <SelectItem value="atrasada">Atrasadas ({atrasadas})</SelectItem>
            <SelectItem value="en_linea">En línea ({enLinea})</SelectItem>
            <SelectItem value="adelantada">Adelantadas ({adelantadas})</SelectItem>
            <SelectItem value="solo_avance">Solo avance ({soloAvance.length})</SelectItem>
            <SelectItem value="sin_plan">Sin plan ({sinPlan})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState title="Sin viviendas en este filtro" icon={Home} />
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table data-testid="avance-obra-table">
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Vivienda</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Comprador</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Lt</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Mz</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right w-16">Proy.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#F05A28] font-semibold py-3 px-3 text-right w-16">Real</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right w-14">Dif.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 w-40">Comparativo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 w-24">Últ. Act.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 w-24">Estatus</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(r => {
                const isPrograma = r.tracking_mode !== 'avance';
                return (
                <TableRow
                  key={r.id}
                  className={`table-row-hover group cursor-pointer ${r.has_plan && r.status === 'atrasada' && isPrograma ? 'bg-red-50/30' : ''}`}
                  onClick={() => onSelect(r)}
                  data-testid={`property-row-${r.id}`}
                >
                  <TableCell className="py-2.5 px-3 text-[13px] font-medium">{r.modelo || '—'}</TableCell>
                  <TableCell className="py-2.5 px-3 text-[13px] text-slate-500 truncate max-w-[180px]">{r.name || '—'}</TableCell>
                  <TableCell className="py-2.5 px-3 text-[13px] tabular-nums text-center">{r.lot}</TableCell>
                  <TableCell className="py-2.5 px-3 text-[13px] font-semibold text-center">{r.block}</TableCell>
                  <TableCell className="py-2.5 px-3 text-right text-xs tabular-nums">
                    {!isPrograma ? <span className="text-slate-300">—</span> : r.has_plan ? `${r.projected}%` : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-right text-xs tabular-nums font-bold text-[#F05A28]">
                    {r.has_plan ? `${r.actual}%` : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-right">
                    {!isPrograma ? <span className="text-slate-300 text-[10px]">—</span> : r.has_plan ? <VarianceText value={r.variance} /> : <span className="text-slate-300 text-[10px]">—</span>}
                  </TableCell>
                  <TableCell className="py-2.5 px-3">
                    {!isPrograma ? (
                      r.has_plan ? <SingleBar value={r.actual} /> : <span className="text-[10px] text-slate-300">Sin plan</span>
                    ) : (
                      r.has_plan ? <DualBar projected={r.projected} actual={r.actual} /> : <span className="text-[10px] text-slate-300">Sin plan</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-[10px] tabular-nums text-slate-400">
                    {r.last_update ? formatDate(r.last_update) : '—'}
                  </TableCell>
                  <TableCell className="py-2.5 px-3">
                    {isPrograma ? <StatusBadge status={r.status} hasPlan={r.has_plan} /> : (
                      r.has_plan ? <Badge variant="outline" className="text-[9px] text-slate-500 border-slate-200">{r.actual}%</Badge> : <span className="text-slate-300 text-[10px]">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-1 px-2">
                    <ArrowRight className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════
   DETAIL VIEW — Single Property
   ══════════════════════════════════════════════ */
function DetailView({ prop, plan, summary, loading, onClose, onInherit, onUpdate, onModeChange }) {
  const [collapsedCats, setCollapsedCats] = useState({});
  const mode = summary?.tracking_mode || 'programa';
  const isPrograma = mode === 'programa';
  const proy = summary?.projected_total || 0;
  const real = summary?.actual_total || 0;
  const variance = summary?.variance || 0;
  const readOnly = true; // Avance General is always read-only

  // Group by category preserving order
  const categories = [];
  const seen = new Set();
  for (const item of plan) {
    const cn = item.category_name;
    if (cn && !seen.has(cn)) {
      seen.add(cn);
      categories.push({
        name: cn,
        items: plan.filter(x => x.category_name === cn),
      });
    }
  }

  // Calculate per-category subtotals
  for (const cat of categories) {
    cat.weightSum = cat.items.reduce((s, i) => s + (i.weight_percent || 0), 0);
    cat.weightedProy = cat.items.reduce((s, i) => s + (i.weighted_projected || 0), 0);
    cat.weightedReal = cat.items.reduce((s, i) => s + (i.weighted_actual || 0), 0);
    cat.proyPct = cat.weightSum > 0 ? Math.round(cat.weightedProy / cat.weightSum * 100) : 0;
    cat.realPct = cat.weightSum > 0 ? Math.round(cat.weightedReal / cat.weightSum * 100) : 0;
    cat.variance = cat.realPct - cat.proyPct;
    cat.completedCount = cat.items.filter(i => (i.actual_percent || 0) >= 100).length;
  }

  const toggleCat = (name) => setCollapsedCats(c => ({ ...c, [name]: !c[name] }));

  return (
    <div className="space-y-6" data-testid="avance-obra-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClose} data-testid="back-button">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Lote {prop.lot} Mz {prop.block}
          </h1>
          <p className="text-[13px] text-slate-400">{prop.modelo} · {prop.name || 'Sin comprador'}</p>
        </div>
        {plan.length > 0 && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="tracking-mode-toggle">
              <Switch checked={isPrograma} onCheckedChange={v => onModeChange(v ? 'programa' : 'avance')} className="scale-75" />
              <span className="text-[11px] text-slate-500">{isPrograma ? 'Con programa' : 'Solo avance'}</span>
            </label>
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-[400px] rounded-xl" />
        </div>
      ) : plan.length === 0 ? (
        /* Empty state */
        <Card className="rounded-xl border bg-white">
          <CardContent className="py-16 text-center">
            <HardHat className="h-14 w-14 text-slate-200 mx-auto mb-4" />
            <p className="text-sm font-medium text-slate-600 mb-1">Sin plan de obra</p>
            <p className="text-xs text-slate-400 mb-6 max-w-xs mx-auto">
              Inicializa el plan heredando los conceptos del catálogo maestro.
            </p>
            <Button onClick={onInherit} data-testid="init-plan-btn">
              <Download className="h-4 w-4 mr-1.5" /> Inicializar Plan de Obra
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Summary header ── */}
          <Card className="rounded-xl border bg-white" data-testid="summary-card">
            <CardContent className="p-5">
              {isPrograma ? (
                <>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Proyectado</p>
                      <p className="text-3xl font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{proy.toFixed(1)}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-[#F05A28] mb-1">Real</p>
                      <p className="text-3xl font-bold tabular-nums text-[#F05A28]" style={{ fontFamily: 'Space Grotesk' }}>{real.toFixed(1)}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Diferencia</p>
                      <p className={`text-3xl font-bold tabular-nums ${variance > 0 ? 'text-emerald-600' : variance < 0 ? 'text-red-500' : 'text-slate-500'}`} style={{ fontFamily: 'Space Grotesk' }}>
                        {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 space-y-1.5">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-slate-400 w-16 text-right shrink-0">Proyectado</span>
                      <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-slate-400 transition-all duration-500" style={{ width: `${proy}%` }} />
                      </div>
                      <span className="text-[10px] tabular-nums text-slate-500 w-10 shrink-0">{proy.toFixed(1)}%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-[#F05A28] w-16 text-right shrink-0 font-medium">Real</span>
                      <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-[#F05A28] transition-all duration-500" style={{ width: `${real}%` }} />
                      </div>
                      <span className="text-[10px] tabular-nums text-[#F05A28] font-semibold w-10 shrink-0">{real.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-1.5 mt-4 text-[11px]">
                    {variance > 1 ? (
                      <><TrendingUp className="h-3.5 w-3.5 text-emerald-600" /><span className="text-emerald-600 font-medium">Adelantada</span></>
                    ) : variance < -1 ? (
                      <><TrendingDown className="h-3.5 w-3.5 text-red-500" /><span className="text-red-500 font-medium">Atrasada</span></>
                    ) : (
                      <><Check className="h-3.5 w-3.5 text-blue-500" /><span className="text-blue-500 font-medium">En tiempo</span></>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-[#F05A28] mb-1">Avance Real</p>
                    <p className="text-4xl font-bold tabular-nums text-[#F05A28]" style={{ fontFamily: 'Space Grotesk' }}>{real.toFixed(1)}%</p>
                  </div>
                  <div className="mt-5">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-[#F05A28] w-16 text-right shrink-0 font-medium">Avance</span>
                      <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-[#F05A28] transition-all duration-500" style={{ width: `${real}%` }} />
                      </div>
                      <span className="text-[10px] tabular-nums text-[#F05A28] font-semibold w-10 shrink-0">{real.toFixed(1)}%</span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* ── Concept table grouped by category ── */}
          <Card className="rounded-xl border bg-white overflow-clip" data-testid="plan-table-card">
            <CardHeader className="pb-2 px-5">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <HardHat className="h-4 w-4 text-[#F05A28]" /> Plan y Avance de Obra
                <Badge variant="outline" className="text-[9px] font-normal text-slate-400 border-slate-200 ml-1">
                  {plan.length} conceptos
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                <Table data-testid="plan-detail-table" className="table-fixed">
                  <TableHeader className="sticky top-0 z-20">
                    <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 w-8" />
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 whitespace-nowrap">Partida / Concepto</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right w-[60px] whitespace-nowrap">Peso</TableHead>
                      {isPrograma && <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right w-[70px] whitespace-nowrap">% Proy.</TableHead>}
                      {isPrograma && <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 w-[90px] whitespace-nowrap">F. Proy.</TableHead>}
                      <TableHead className="text-[10px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-2 text-right w-[70px] whitespace-nowrap">% Real</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 w-[90px] whitespace-nowrap">F. Real</TableHead>
                      {isPrograma && <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right w-[50px] whitespace-nowrap">Dif.</TableHead>}
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 w-[120px] whitespace-nowrap">{isPrograma ? 'Comparativo' : 'Avance'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map(cat => {
                      const isCollapsed = collapsedCats[cat.name];
                      return [
                        /* ── Category header ── */
                        <TableRow
                          key={`cat-${cat.name}`}
                          className="bg-slate-50/80 border-t border-slate-200/60 hover:bg-slate-100/50"
                          data-testid={`cat-header-${cat.name}`}
                        >
                          <TableCell className="py-2.5 px-3">
                            <button
                              onClick={() => toggleCat(cat.name)}
                              className="h-5 w-5 rounded flex items-center justify-center text-slate-400 hover:text-slate-700"
                            >
                              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          </TableCell>
                          <TableCell className="py-2.5 px-3">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{cat.name}</span>
                              <Badge variant="outline" className="text-[8px] font-normal text-slate-400 border-slate-200 py-0">
                                {cat.completedCount}/{cat.items.length}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="py-2.5 px-3 text-right text-[10px] tabular-nums text-slate-400">
                            {cat.weightSum.toFixed(1)}%
                          </TableCell>
                          {isPrograma && <TableCell className="py-2.5 px-3 text-right text-[10px] tabular-nums font-semibold text-slate-600">
                            {cat.proyPct}%
                          </TableCell>}
                          {isPrograma && <TableCell />}
                          <TableCell className="py-2.5 px-3 text-right text-[10px] tabular-nums font-bold text-[#F05A28]">
                            {cat.realPct}%
                          </TableCell>
                          <TableCell />
                          {isPrograma && <TableCell className="py-2.5 px-3 text-right">
                            <VarianceText value={cat.variance} />
                          </TableCell>}
                          <TableCell className="py-2.5 px-3">
                            {isPrograma ? <DualBar projected={cat.proyPct} actual={cat.realPct} height="h-2" /> : <SingleBar value={cat.realPct} height="h-2" />}
                          </TableCell>
                        </TableRow>,

                        /* ── Concept rows ── */
                        ...(!isCollapsed ? cat.items.map(item => {
                          const isComplete = (item.actual_percent || 0) >= 100;
                          const rowBg = item.variance < -10 ? 'bg-red-50/40' : item.variance > 10 ? 'bg-emerald-50/30' : '';
                          return (
                            <TableRow
                              key={item.id}
                              className={`table-row-hover ${rowBg}`}
                              data-testid={`concept-row-${item.id}`}
                            >
                              <TableCell className="py-2.5 px-3 text-center">
                                {isComplete && (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mx-auto" />
                                )}
                              </TableCell>
                              <TableCell className="py-2.5 px-3 pl-8">
                                <span className={`text-[13px] ${isComplete ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                                  {item.concept_name}
                                </span>
                              </TableCell>
                              <TableCell className="py-2.5 px-3 text-right text-[10px] tabular-nums text-slate-400">
                                {item.weight_percent}%
                              </TableCell>
                              {isPrograma && <TableCell className="py-2.5 px-3 text-right">
                                <span className="text-[13px] tabular-nums text-slate-600" data-testid={`proy-${item.id}`}>{item.projected_percent || 0}%</span>
                              </TableCell>}
                              {isPrograma && <TableCell className="py-2.5 px-3">
                                <span className="text-[11px] text-slate-500" data-testid={`proy-date-${item.id}`}>{item.projected_date ? formatDate(item.projected_date) : <span className="text-slate-300">—</span>}</span>
                              </TableCell>}
                              <TableCell className="py-2.5 px-3 text-right">
                                <span className="text-[13px] tabular-nums font-bold text-[#F05A28]" data-testid={`real-${item.id}`}>{item.actual_percent || 0}%</span>
                              </TableCell>
                              <TableCell className="py-2.5 px-3">
                                <span className="text-[11px] text-slate-500" data-testid={`real-date-${item.id}`}>{item.actual_date ? formatDate(item.actual_date) : <span className="text-slate-300">—</span>}</span>
                              </TableCell>
                              {isPrograma && <TableCell className="py-2.5 px-3 text-right">
                                <VarianceText value={item.variance} />
                              </TableCell>}
                              <TableCell className="py-2.5 px-3">
                                {isPrograma ? <DualBar projected={item.projected_percent || 0} actual={item.actual_percent || 0} /> : <SingleBar value={item.actual_percent || 0} />}
                              </TableCell>
                            </TableRow>
                          );
                        }) : []),
                      ];
                    })}
                  </TableBody>
                </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════════ */

/* Dual progress bar: projected (gray) + real (orange) stacked */
function DualBar({ projected, actual, height = 'h-1.5' }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="space-y-1" data-testid="dual-bar">
          <div className={`${height} rounded-full bg-slate-100 overflow-hidden`}>
            <div className="h-full rounded-full bg-slate-400 transition-all duration-300" style={{ width: `${Math.min(projected, 100)}%` }} />
          </div>
          <div className={`${height} rounded-full bg-slate-100 overflow-hidden`}>
            <div className="h-full rounded-full bg-[#F05A28] transition-all duration-300" style={{ width: `${Math.min(actual, 100)}%` }} />
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">
        <div className="text-xs space-y-0.5">
          <div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-full bg-slate-400" /><span>Proy: {projected}%</span></div>
          <div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-full bg-[#F05A28]" /><span>Real: {actual}%</span></div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/* Single progress bar: only real (orange) */
function SingleBar({ value, height = 'h-1.5' }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={`${height} rounded-full bg-slate-100 overflow-hidden`} data-testid="single-bar">
          <div className="h-full rounded-full bg-[#F05A28] transition-all duration-300" style={{ width: `${Math.min(value, 100)}%` }} />
        </div>
      </TooltipTrigger>
      <TooltipContent side="top"><span className="text-xs">Avance: {value}%</span></TooltipContent>
    </Tooltip>
  );
}


/* Variance text with color */
function VarianceText({ value }) {
  if (value == null || Math.abs(value) < 0.1) return <span className="text-[10px] text-slate-300">—</span>;
  const color = value > 0 ? 'text-emerald-600' : 'text-red-500';
  return (
    <span className={`text-[10px] tabular-nums font-semibold ${color}`}>
      {value > 0 ? '+' : ''}{typeof value === 'number' ? value.toFixed(1) : value}%
    </span>
  );
}

/* Status badge */
function StatusBadge({ status, hasPlan }) {
  if (!hasPlan) return <Badge variant="outline" className="bg-slate-50 text-slate-400 border-slate-200 text-[9px]">Sin plan</Badge>;
  if (status === 'atrasada') return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-[9px]">Atrasada</Badge>;
  if (status === 'adelantada') return <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]">Adelantada</Badge>;
  return <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 text-[9px]">En línea</Badge>;
}

/* Inline editable cell with quick % buttons */
const QUICK_PCTS = [25, 50, 75, 100];

function Editable({ value, onSave, type = 'number', suffix = '', bold, testId }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value ?? '');

  const commit = () => {
    setEditing(false);
    if (type === 'number') {
      const v = Math.min(100, Math.max(0, parseFloat(local) || 0));
      if (v !== (value || 0)) onSave(v);
    } else {
      if (local !== (value || '')) onSave(local || null);
    }
  };

  const handleQuick = (pct) => {
    setEditing(false);
    onSave(pct);
  };

  if (editing && type === 'number' && suffix === '%') {
    return (
      <div className="relative">
        <input
          autoFocus
          type="number"
          min="0"
          max="100"
          className="h-7 w-16 text-[12px] tabular-nums border border-slate-300 rounded-lg px-2 outline-none focus:ring-1 focus:ring-[#F05A28] bg-white text-right"
          value={local}
          onChange={e => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setLocal(value ?? ''); setEditing(false); }
          }}
        />
        <div className="absolute left-0 top-full mt-1 flex gap-0.5 z-30 bg-white rounded-lg shadow-lg border border-slate-200 p-1">
          {QUICK_PCTS.map(p => (
            <button
              key={p}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleQuick(p)}
              className={`h-6 min-w-[32px] px-1.5 rounded-md text-[10px] font-semibold tabular-nums transition-colors ${p === 100 ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
            >
              {p === 100 ? '\u2713' : `${p}%`}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        className="h-7 w-24 text-[12px] tabular-nums border border-slate-300 rounded-lg px-2 outline-none focus:ring-1 focus:ring-[#F05A28] bg-white text-right"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setLocal(value ?? ''); setEditing(false); }
        }}
      />
    );
  }

  if (type === 'date') {
    return (
      <span
        onClick={() => { setLocal(value ?? ''); setEditing(true); }}
        className="editable-cell cursor-pointer text-[11px] text-slate-500"
        data-testid={testId}
      >
        {value ? formatDate(value) : <span className="text-slate-300">—</span>}
      </span>
    );
  }

  const display = (value || 0) > 0 ? `${value}${suffix}` : null;
  return (
    <span
      onClick={() => { setLocal(value ?? ''); setEditing(true); }}
      className={`editable-cell cursor-pointer text-[13px] tabular-nums ${bold ? 'font-bold text-[#F05A28]' : 'text-slate-600'}`}
      data-testid={testId}
    >
      {display || <span className="text-slate-300">—</span>}
    </span>
  );
}



/* ══════════════════════════════════════════════
   WEEKLY PROGRESS TAB
   ══════════════════════════════════════════════ */
function WeeklyProgressTab({ canEdit, onSynced }) {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [captureProperty, setCaptureProperty] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectPropOpen, setSelectPropOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [editWeekOpen, setEditWeekOpen] = useState(false);

  const loadWeeks = useCallback(async () => {
    try { setWeeks(await fetchWeeklyProgressWeeks()); }
    catch { toast.error('Error al cargar semanas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadWeeks(); }, [loadWeeks]);

  const openWeek = async (wl) => {
    try { setSelectedWeek(wl); setWeekData(await fetchWeeklyProgress(wl)); }
    catch { toast.error('Error'); }
  };

  // Capture view — full catalog for a property within a week
  if (captureProperty && weekData) {
    return (
      <WeeklyCaptureView
        weekLabel={selectedWeek}
        weekDates={{ start_date: weekData.start_date || '', end_date: weekData.end_date || '' }}
        property={captureProperty}
        existingEntries={(weekData.properties.find(p => p.property_id === captureProperty.id)?.concepts) || []}
        onClose={() => { setCaptureProperty(null); openWeek(selectedWeek); }}
        canEdit={canEdit}
      />
    );
  }

  // Week detail view
  if (weekData) {
    const totalWeekly = weekData.properties.reduce((s, p) => s + p.total_weighted_weekly, 0);
    const avgCumulative = weekData.properties.length > 0 ? weekData.properties.reduce((s, p) => s + p.total_weighted_cumulative, 0) / weekData.properties.length : 0;
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setWeekData(null); setSelectedWeek(null); loadWeeks(); }}><ChevronLeft className="h-4 w-4" /></Button>
          <div className="flex-1">
            <h2 className="text-[20px] font-semibold text-slate-900" style={{ fontFamily: 'Space Grotesk' }}>{weekData.week_label}</h2>
            <p className="text-[12px] text-slate-400">{weekData.start_date && weekData.end_date ? `${formatDate(weekData.start_date)} — ${formatDate(weekData.end_date)} · ` : ''}{weekData.total_properties} viviendas · {weekData.total_entries} registros</p>
          </div>
          {canEdit && weekData.status === 'draft' && <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-700" onClick={() => setEditWeekOpen(true)} title="Editar semana"><Pencil className="h-4 w-4" /></Button>}
          <Badge variant="outline" className={`text-xs px-3 py-1 ${weekData.status === 'verified' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}`}>
            {weekData.status === 'verified' ? 'Verificado' : 'Borrador'}
          </Badge>
          {canEdit && <Button size="sm" onClick={() => setSelectPropOpen(true)}><Plus className="h-3.5 w-3.5 mr-1.5" /> Capturar Avance</Button>}
          {canEdit && weekData.week_id && weekData.status === 'draft' && (
            <Button size="sm" variant="outline" onClick={async () => {
              try { 
                const result = await changeWeekStatus(weekData.week_id, 'verified');
                const sr = result.sync_result;
                if (sr) {
                  const parts = [];
                  if (sr.synced > 0) parts.push(`${sr.synced} actualizados`);
                  if (sr.created > 0) parts.push(`${sr.created} creados`);
                  if (sr.inherited > 0) parts.push(`${sr.inherited} conceptos heredados del catálogo`);
                  toast.success(`Sincronizado: ${sr.total_entries} registros — ${parts.join(', ')}`);
                } else {
                  toast.success('Semana verificada');
                }
                await openWeek(selectedWeek); 
                loadWeeks();
                if (onSynced) onSynced(); 
              }
              catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
            }}><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Verificar y Sincronizar</Button>
          )}
          {canEdit && weekData.week_id && (weekData.status === 'verified' || weekData.status === 'closed') && (
            <Button size="sm" variant="ghost" className="text-slate-500" onClick={async () => {
              try { 
                await changeWeekStatus(weekData.week_id, 'draft'); 
                toast.success('Regresada a borrador'); 
                await openWeek(selectedWeek); 
                loadWeeks();
              }
              catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
            }}>Regresar a Borrador</Button>
          )}
          {weekData.total_entries > 0 && (
            <Button size="sm" variant="outline" onClick={() => setCloneOpen(true)}><Copy className="h-3.5 w-3.5 mr-1.5" /> Clonar Semana</Button>
          )}
          {canEdit && weekData.status === 'draft' && weekData.week_id && (
            <Button size="sm" variant="ghost" className="text-red-500 ml-auto" onClick={async () => {
              if (!confirm(`¿Eliminar ${weekData.week_label} y todos sus registros?`)) return;
              try { await deleteProgressWeek(weekData.week_id); toast.success('Semana eliminada'); setWeekData(null); setSelectedWeek(null); loadWeeks(); }
              catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
            }}><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eliminar Semana</Button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KPICard label="Viviendas" value={weekData.total_properties} icon={Home} compact />
          <KPICard label="Registros" value={weekData.total_entries} icon={HardHat} compact />
          <KPICard label="Av. Semanal Pond." value={`${totalWeekly.toFixed(1)}%`} icon={TrendingUp} compact />
          <KPICard label="Av. Acum. Prom." value={`${avgCumulative.toFixed(1)}%`} icon={CheckCircle2} compact />
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 flex items-center gap-3">
          <AlertTriangle className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <p className="text-[10px] text-slate-500">Solo registro operativo. No genera pagos ni egresos. Soporte para estimaciones de destajo.</p>
        </div>
        {weekData.properties.length > 0 && (
          <Card className="rounded-xl border bg-white overflow-clip">
            <Table>
              <TableHeader className="sticky top-0 z-20">
                <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Vivienda</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-center">Conceptos</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">Av. Anterior</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-3 text-right">Av. Semana</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-700 font-semibold py-2.5 px-3 text-right">Total Vivienda</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {weekData.properties.map(p => (
                  <TableRow key={p.property_id} className="cursor-pointer table-row-hover group" onClick={() => {
                    const prop = { id: p.property_id, lot: p.property_lot, block: p.property_block, name: p.property_name };
                    setCaptureProperty(prop);
                  }}>
                    <TableCell className="py-2.5 px-3 text-[13px] font-semibold">Lt {p.property_lot} Mz {p.property_block}</TableCell>
                    <TableCell className="py-2.5 px-3 text-center text-[12px] tabular-nums">{p.concepts_count}</TableCell>
                    <TableCell className="py-2.5 px-3 text-right text-[12px] tabular-nums">{((p.avance_total_vivienda || 0) - (p.total_weighted_weekly || 0)).toFixed(1)}%</TableCell>
                    <TableCell className="py-2.5 px-3 text-right text-[13px] tabular-nums font-bold text-[#F05A28]">{p.total_weighted_weekly > 0 ? '+' : ''}{p.total_weighted_weekly.toFixed(1)}%</TableCell>
                    <TableCell className="py-2.5 px-3 text-right text-[13px] tabular-nums font-bold text-slate-800">{(p.avance_total_vivienda || 0).toFixed(1)}%</TableCell>
                    <TableCell className="py-1 px-2">
                      <div className="flex items-center gap-1">
                        {canEdit && weekData.status === 'draft' && (
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100" onClick={(e) => {
                            e.stopPropagation();
                            if (!confirm(`¿Eliminar avance de Lt ${p.property_lot} Mz ${p.property_block} de esta semana?`)) return;
                            deletePropertyFromWeek(selectedWeek, p.property_id).then(() => { toast.success('Vivienda eliminada de la semana'); openWeek(selectedWeek); }).catch(() => toast.error('Error'));
                          }}><Trash2 className="h-3 w-3" /></Button>
                        )}
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
        {selectPropOpen && <SelectPropertyDialog onClose={() => setSelectPropOpen(false)} onSelect={(prop) => { setSelectPropOpen(false); setCaptureProperty(prop); }} />}
        {cloneOpen && <CloneWeekDialog sourceWeek={selectedWeek} onClose={() => setCloneOpen(false)} onCloned={(wl) => { setCloneOpen(false); openWeek(wl); }} />}
        {editWeekOpen && weekData.week_id && (
          <EditWeekDialog
            weekId={weekData.week_id}
            initial={{ week_label: weekData.week_label, start_date: weekData.start_date || '', end_date: weekData.end_date || '' }}
            onClose={() => setEditWeekOpen(false)}
            onSaved={(updated) => { setEditWeekOpen(false); setSelectedWeek(updated.week_label); openWeek(updated.week_label); loadWeeks(); }}
          />
        )}
      </div>
    );
  }

  // Weeks list
  if (loading) return <Skeleton className="h-[300px] rounded-xl" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Avance Semanal</h2>
        {canEdit && <Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-3.5 w-3.5 mr-1.5" /> Nueva Semana</Button>}
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 flex items-center gap-3">
        <AlertTriangle className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <p className="text-[10px] text-slate-500">Módulo independiente del flujo financiero. Avance registrado no significa pago. Soporte operativo para estimaciones.</p>
      </div>
      {weeks.some(w => w.status === 'closed') && canEdit && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid="migrate-closed-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            <p className="text-xs text-amber-700">Hay semanas con estatus "Cerrado" que necesitan migrarse a "Verificado".</p>
          </div>
          <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100 shrink-0" onClick={async () => {
            try {
              const res = await migrateClosedWeeks();
              toast.success(`${res.migrated} semana(s) migrada(s) a Verificado`);
              loadWeeks();
            } catch (err) { toast.error(err?.response?.data?.detail || 'Error al migrar'); }
          }} data-testid="migrate-closed-btn">Migrar a Verificado</Button>
        </div>
      )}
      {weeks.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-12 text-center">
          <Calendar className="h-12 w-12 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-600">Sin avance semanal registrado</p>
          <p className="text-xs text-slate-400">Crea la primera semana para empezar.</p>
        </CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table>
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Semana</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Periodo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Viviendas</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Registros</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Av. Anterior</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#F05A28] font-semibold py-3 px-3 text-right">Av. Semana</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Av. Acumulado</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Estatus</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {weeks.map(w => {
                const go = () => openWeek(w.week_label);
                const st = w.status === 'closed' ? 'verified' : (w.status || 'draft');
                return (
                <TableRow key={w.week_label || w.id} className={`cursor-pointer table-row-hover ${st === 'verified' ? 'bg-emerald-50/30' : ''}`} onClick={go} role="button">
                  <TableCell className="py-2.5 px-3 select-none" onClick={go}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[13px] font-semibold">{w.week_label}</span>
                      {st === 'verified' && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-[11px] text-slate-500 select-none" onClick={go}>{w.start_date && w.end_date ? `${formatDate(w.start_date)} — ${formatDate(w.end_date)}` : '—'}</TableCell>
                  <TableCell className="py-2.5 px-3 text-center text-[12px] tabular-nums select-none" onClick={go}>{w.property_count || 0}</TableCell>
                  <TableCell className="py-2.5 px-3 text-center text-[12px] tabular-nums select-none" onClick={go}>{w.entries || 0}</TableCell>
                  <TableCell className="py-2.5 px-3 text-right text-[12px] tabular-nums select-none" onClick={go}>{w.avg_previous || 0}%</TableCell>
                  <TableCell className="py-2.5 px-3 text-right text-[12px] tabular-nums font-bold text-[#F05A28] select-none" onClick={go}>{w.avg_weekly > 0 ? `+${w.avg_weekly}` : w.avg_weekly || 0}%</TableCell>
                  <TableCell className="py-2.5 px-3 text-right text-[12px] tabular-nums font-semibold select-none" onClick={go}>{w.avg_cumulative || 0}%</TableCell>
                  <TableCell className="py-2.5 px-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                      st === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-500'
                    }`}>{st === 'verified' ? 'Verificado' : 'Borrador'}</span>
                  </TableCell>
                  <TableCell className="py-1 px-2 select-none" onClick={go}><ChevronRight className="h-3.5 w-3.5 text-slate-300" /></TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      {createOpen && <NewWeekDialog onClose={() => setCreateOpen(false)} onCreated={(week) => {
        setCreateOpen(false);
        setSelectedWeek(week.week_label);
        setWeekData({ week_label: week.week_label, start_date: week.start_date, end_date: week.end_date, status: 'draft', week_id: week.id, properties: [], total_entries: 0, total_properties: 0 });
      }} />}
    </div>
  );
}

/* ── Select Property Dialog ── */
function SelectPropertyDialog({ onClose, onSelect }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { fetchProperties().then(setProperties).catch(() => {}).finally(() => setLoading(false)); }, []);
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[70vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Seleccionar Vivienda</DialogTitle></DialogHeader>
        {loading ? <Skeleton className="h-40" /> : (
          <div className="space-y-1 py-2">
            {properties.map(p => (
              <button key={p.id} onClick={() => onSelect(p)} className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 flex items-center justify-between group transition-colors">
                <div>
                  <p className="text-[13px] font-semibold text-slate-800">Lt {p.lot} Mz {p.block}</p>
                  <p className="text-[11px] text-slate-400">{p.name || p.modelo}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Weekly Capture View — Full catalog for a property ── */
function WeeklyCaptureView({ weekLabel, weekDates, property, existingEntries, onClose, canEdit }) {
  const [catalog, setCatalog] = useState([]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchWorkCatalog(null, true),
      fetchWorkPlanSummary(property.id).catch(() => null),
    ]).then(([items, summary]) => {
      setCatalog(items);
      // Build work plan lookup for actual progress
      const planLookup = {};
      if (summary?.items) {
        for (const pi of summary.items) {
          planLookup[pi.work_catalog_item_id] = pi.actual_percent || 0;
        }
      }
      // Pre-fill from existing entries, fallback to work plan
      const vals = {};
      for (const e of existingEntries) {
        vals[e.work_catalog_item_id] = { current: e.current_progress_percent, previous: e.previous_progress_percent, id: e.id, notes: e.notes || '' };
      }
      // For items not in existing entries, set previous from work plan
      for (const item of items) {
        if (!vals[item.id] && planLookup[item.id]) {
          vals[item.id] = { current: '', previous: planLookup[item.id], notes: '' };
        }
      }
      setValues(vals);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [existingEntries, property.id]);

  const setValue = (itemId, field, val) => {
    setValues(v => ({ ...v, [itemId]: { ...v[itemId], [field]: val } }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const entries = [];
      for (const item of catalog) {
        const v = values[item.id];
        if (v && v.current !== undefined && v.current !== '' && v.current !== null) {
          entries.push({
            week_label: weekLabel,
            start_date: weekDates.start_date || '',
            end_date: weekDates.end_date || '',
            property_id: property.id,
            work_catalog_item_id: item.id,
            current_progress_percent: parseFloat(v.current) || 0,
            notes: v.notes || null,
          });
        }
      }
      if (entries.length > 0) {
        await batchCreateWeeklyProgress(entries);
        toast.success(`${entries.length} registros guardados`);
      }
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  // Group by category
  const seen = new Set();
  const categories = [];
  for (const c of catalog) {
    if (!seen.has(c.category_name)) {
      seen.add(c.category_name);
      categories.push({ name: c.category_name, items: catalog.filter(x => x.category_name === c.category_name) });
    }
  }

  if (loading) return <Skeleton className="h-[400px] rounded-xl" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}><ChevronLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h2 className="text-[20px] font-semibold text-slate-900" style={{ fontFamily: 'Space Grotesk' }}>Lt {property.lot} Mz {property.block} — {weekLabel}</h2>
          <p className="text-[12px] text-slate-400">{property.name || property.modelo} · Captura de avance semanal</p>
        </div>
        {canEdit && <Button onClick={handleSaveAll} disabled={saving}>{saving ? 'Guardando...' : 'Guardar Avance'}</Button>}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 flex items-center gap-3">
        <AlertTriangle className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <p className="text-[10px] text-slate-500">Captura el avance acumulado actual (%) de cada concepto. El avance anterior y el semanal se calculan automáticamente.</p>
      </div>

      <Card className="rounded-xl border bg-white overflow-clip">
        <Table className="table-fixed">
          <TableHeader className="sticky top-0 z-20">
            <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 whitespace-nowrap">Partida / Concepto</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right w-[60px] whitespace-nowrap">Peso</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right w-[80px] whitespace-nowrap">Av. Anterior</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-2 w-[90px] whitespace-nowrap">% Actual</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right w-[70px] whitespace-nowrap">Semanal</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 w-[120px] whitespace-nowrap">Obs.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map(cat => [
              <TableRow key={`cat-${cat.name}`} className="bg-slate-50/50">
                <TableCell colSpan={6} className="py-2 px-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{cat.name}</span>
                </TableCell>
              </TableRow>,
              ...cat.items.map(item => {
                const v = values[item.id] || {};
                const prev = v.previous ?? 0;
                const curr = parseFloat(v.current) || 0;
                const weekly = curr - prev;
                return (
                  <TableRow key={item.id}>
                    <TableCell className="py-1.5 px-2 pl-6 text-[12px] text-slate-700">{item.concept_name}</TableCell>
                    <TableCell className="py-1.5 px-2 text-right text-[10px] tabular-nums text-slate-400">{item.weight_percent}%</TableCell>
                    <TableCell className="py-1.5 px-2 text-right text-[11px] tabular-nums text-slate-500">{prev}%</TableCell>
                    <TableCell className="py-1.5 px-2">
                      {canEdit ? (
                        <Input
                          type="number" min="0" max="100" step="0.1"
                          className="h-7 w-full text-[12px] text-right tabular-nums"
                          value={v.current ?? ''}
                          onChange={e => setValue(item.id, 'current', e.target.value)}
                        />
                      ) : (
                        <span className="text-[12px] tabular-nums font-bold text-[#F05A28]">{curr}%</span>
                      )}
                    </TableCell>
                    <TableCell className="py-1.5 px-2 text-right text-[11px] tabular-nums font-semibold">
                      {weekly > 0 ? <span className="text-[#F05A28]">+{weekly.toFixed(1)}%</span> : weekly < 0 ? <span className="text-red-500">{weekly.toFixed(1)}%</span> : '—'}
                    </TableCell>
                    <TableCell className="py-1.5 px-2">
                      {canEdit ? (
                        <Input className="h-7 text-[10px]" placeholder="Obs." value={v.notes || ''} onChange={e => setValue(item.id, 'notes', e.target.value)} />
                      ) : (
                        <span className="text-[10px] text-slate-400">{v.notes || '—'}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              }),
            ])}
          </TableBody>
        </Table>
      </Card>

      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={handleSaveAll} disabled={saving} size="lg">{saving ? 'Guardando...' : 'Guardar Avance Semanal'}</Button>
        </div>
      )}
    </div>
  );
}

function EditWeekDialog({ weekId, initial, onClose, onSaved }) {
  const [form, setForm] = useState({ week_label: initial.week_label || '', start_date: initial.start_date || '', end_date: initial.end_date || '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateProgressWeek(weekId, form);
      toast.success('Semana actualizada');
      onSaved(updated);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Editar Semana</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Etiqueta</Label><Input value={form.week_label} onChange={e => set('week_label', e.target.value)} className="mt-1 h-9" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Fecha inicio</Label><Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="mt-1 h-9 text-sm" /></div>
            <div><Label className="text-xs">Fecha fin</Label><Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className="mt-1 h-9 text-sm" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !form.week_label}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloneWeekDialog({ sourceWeek, onClose, onCloned }) {
  const srcEnd = new Date();
  const nextStart = new Date(srcEnd); nextStart.setDate(nextStart.getDate() + 1);
  const nextEnd = new Date(nextStart); nextEnd.setDate(nextEnd.getDate() + 6);
  const fmt = (d) => d.toISOString().substring(0, 10);
  const [form, setForm] = useState({
    week_label: sourceWeek.replace(/\d+/, (n) => parseInt(n) + 1),
    start_date: fmt(nextStart),
    end_date: fmt(nextEnd),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleClone = async () => {
    setSaving(true);
    try {
      await cloneWeekProgress({ source_week_label: sourceWeek, ...form });
      toast.success(`Semana clonada: ${form.week_label}`);
      onCloned(form.week_label);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-[#F05A28]" /> Clonar Semana</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="bg-slate-50 rounded-lg p-3 text-xs"><p className="text-slate-500">Se clonan todos los registros de <strong>{sourceWeek}</strong>. El avance actual se convierte en avance anterior.</p></div>
          <div><Label className="text-xs">Nueva semana</Label><Input value={form.week_label} onChange={e => set('week_label', e.target.value)} className="mt-1 h-9" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Inicio</Label><Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="mt-1 h-9 text-sm" /></div>
            <div><Label className="text-xs">Fin</Label><Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className="mt-1 h-9 text-sm" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleClone} disabled={saving || !form.week_label}>{saving ? 'Clonando...' : 'Clonar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewWeekDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ week_label: '', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isValid = form.week_label && form.start_date && form.end_date;
  const handleCreate = async () => {
    setSaving(true);
    try {
      const week = await createProgressWeek(form);
      toast.success('Semana creada');
      onCreated(week);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Nueva Semana de Avance</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Etiqueta de semana</Label><Input value={form.week_label} onChange={e => set('week_label', e.target.value)} placeholder="Semana 17 — May 2026" className="mt-1 h-9" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Fecha inicio *</Label><Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="mt-1 h-9 text-sm" /></div>
            <div><Label className="text-xs">Fecha fin *</Label><Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className="mt-1 h-9 text-sm" /></div>
          </div>
          {!isValid && form.week_label && <p className="text-[10px] text-amber-600">Las fechas de inicio y fin son obligatorias</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!isValid || saving} onClick={handleCreate}>{saving ? 'Creando...' : 'Crear'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
